import React, { useState } from 'react';
import { PageLayout } from '../components/layout/PageLayout';

export default function Contact() {
  const [form, setForm] = useState({ name: '', email: '', company: '', message: '', product: '' });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return;
    setSubmitting(true);
    // Placeholder — wire to backend when ready
    await new Promise(r => setTimeout(r, 800));
    setSubmitted(true);
    setSubmitting(false);
  };

  return (
    <PageLayout
      title="Contact Sales"
      subtitle="Talk to our team about your payment infrastructure needs."
      badge="GET IN TOUCH"
    >
      <div className="max-w-5xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-12">
          
          {/* Form */}
          <div className="md:col-span-3">
            {submitted ? (
              <div className="bg-green-50 border border-green-200 rounded-2xl p-12 text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="ri-check-line text-3xl text-green-600 w-8 h-8 flex items-center justify-center" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">We'll be in touch shortly</h3>
                <p className="text-gray-600">A member of our team will reach out within 1 business day.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">Full Name *</label>
                    <input type="text" required value={form.name} onChange={e => setForm({...form, name: e.target.value})}
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all outline-none text-sm" placeholder="Jane Smith" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">Work Email *</label>
                    <input type="email" required value={form.email} onChange={e => setForm({...form, email: e.target.value})}
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all outline-none text-sm" placeholder="jane@company.com" />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">Company</label>
                    <input type="text" value={form.company} onChange={e => setForm({...form, company: e.target.value})}
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all outline-none text-sm" placeholder="Acme Inc." />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">Product Interest</label>
                    <select value={form.product} onChange={e => setForm({...form, product: e.target.value})}
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all outline-none text-sm bg-white">
                      <option value="">Select a product</option>
                      <option value="payments">Payments</option>
                      <option value="subscriptions">Subscriptions</option>
                      <option value="connect">Connect</option>
                      <option value="tapn">Tapn</option>
                      <option value="radar">Radar</option>
                      <option value="billing">Billing</option>
                      <option value="other">Other / General</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">How can we help? *</label>
                  <textarea required rows={5} value={form.message} onChange={e => setForm({...form, message: e.target.value})}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all outline-none text-sm resize-none"
                    placeholder="Tell us about your business, current payment stack, and what you're looking to achieve..." />
                </div>
                <button type="submit" disabled={submitting}
                  className="w-full py-3.5 bg-blue-600 text-white font-bold rounded-xl hover:bg-blue-700 transition-colors disabled:opacity-60 cursor-pointer"
                  style={{ boxShadow: '0 4px 16px rgba(37,99,235,0.25)' }}>
                  {submitting ? 'Sending...' : 'Submit Request'}
                </button>
              </form>
            )}
          </div>

          {/* Sidebar */}
          <div className="md:col-span-2 space-y-6">
            <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
              <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center mb-4">
                <i className="ri-customer-service-2-line text-blue-600 w-5 h-5 flex items-center justify-center" />
              </div>
              <h3 className="font-bold text-gray-900 mb-1">Sales Team</h3>
              <p className="text-sm text-gray-500 mb-3">For pricing, demos, and enterprise inquiries.</p>
              <a href="mailto:sales@gopaygent.com" className="text-sm text-blue-600 font-semibold hover:underline">sales@gopaygent.com</a>
            </div>
            <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
              <div className="w-10 h-10 bg-teal-50 rounded-xl flex items-center justify-center mb-4">
                <i className="ri-code-s-slash-line text-teal-600 w-5 h-5 flex items-center justify-center" />
              </div>
              <h3 className="font-bold text-gray-900 mb-1">Developer Support</h3>
              <p className="text-sm text-gray-500 mb-3">For API integration help and technical questions.</p>
              <a href="mailto:dev@gopaygent.com" className="text-sm text-teal-600 font-semibold hover:underline">dev@gopaygent.com</a>
            </div>
            <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
              <div className="w-10 h-10 bg-amber-50 rounded-xl flex items-center justify-center mb-4">
                <i className="ri-building-line text-amber-600 w-5 h-5 flex items-center justify-center" />
              </div>
              <h3 className="font-bold text-gray-900 mb-1">Headquarters</h3>
              <p className="text-sm text-gray-500">Wheelcall LLC<br />Atlanta, GA</p>
            </div>
          </div>

        </div>
      </div>
    </PageLayout>
  );
}
